/**
 * Shared constants and helpers for Coco tool keys.
 * Coco tools are Cotera-included tools with keys prefixed by coco/
 * (e.g. coco/search-cotera, coco/load-resource, coco/sales-assistant).
 */

export const COCO_TOOL_KEY_PREFIX = 'coco/' as const;
export const COCO_AGENT_TOOL_KEY_PREFIX = 'coco/agent-' as const;
export const COCO_WORKFLOW_TOOL_KEY_PREFIX = 'coco/workflow-' as const;

/** Aggregated tool key for per-agent `coco/agent-*` invocations (metrics, dashboards). */
export const COCO_COTERA_AGENT_TOOL_KEY = 'coco/cotera-agent' as const;
/** Aggregated tool key for per-workflow `coco/workflow-*` invocations (metrics, dashboards). */
export const COCO_COTERA_WORKFLOW_TOOL_KEY = 'coco/cotera-workflow' as const;

/** Individual coco tool key constants for use when importing. */
export const COCO_SEARCH_TOOL_KEY = 'coco/search-cotera' as const;
export const COCO_USER_OPTIONS_TOOL_KEY = 'coco/user-options' as const;
/** Interrupt: user picks a folder UUID from Private/Shared folder browser. */
export const COCO_SELECT_FOLDER_TOOL_KEY = 'coco/select-folder' as const;
/** Create a new agent (instructions + model). Optional folderId places the agent in that folder. */
export const COCO_CREATE_AGENT_TOOL_KEY = 'coco/create-agent' as const;
/** Edit an existing agent: full document replace, or JSON Pointer + value for targeted edits. */
export const COCO_EDIT_AGENT_TOOL_KEY = 'coco/edit-agent' as const;
export const COCO_READ_DOCUMENT_TOOL_KEY = 'coco/read-document' as const;
/** Read a Cotera document resource as markdown for prompt consumption. */
export const COCO_READ_DOCUMENT_AS_MARKDOWN_TOOL_KEY =
  'coco/read-document-as-markdown' as const;
/** Create a Cotera document resource (TipTap editor state). Optional folderId places the document. */
export const COCO_CREATE_DOCUMENT_TOOL_KEY = 'coco/create-document' as const;
/** Pre-create check: find existing documents/agents similar to one about to be created, so duplicates can be surfaced before writing anything. */
export const COCO_FIND_SIMILAR_RESOURCES_TOOL_KEY =
  'coco/find-similar-resources' as const;
/** Start organizing the library from chat: build (or reuse) the staged folder plan and open the Organize review panel. The entry point when the user asks Coco to organize, outside the Library's Organize button. */
export const COCO_ORGANIZE_LIBRARY_TOOL_KEY = 'coco/organize-library' as const;
/** Read the current staged organize draft (folders + items in the review panel), so Coco can answer questions about it. */
export const COCO_ORGANIZE_READ_DRAFT_TOOL_KEY =
  'coco/organize-read-draft' as const;
/** Stage a move in the organize draft: put a resource into a (draft) folder by name, creating the folder in the draft if needed. Nothing is real until the user confirms. */
export const COCO_ORGANIZE_STAGE_MOVE_TOOL_KEY =
  'coco/organize-stage-move' as const;
/** Add an empty folder to the organize draft (staged; created for real only on confirm). */
export const COCO_ORGANIZE_ADD_FOLDER_TOOL_KEY =
  'coco/organize-add-folder' as const;
/** Remove a folder from the organize draft — to tidy up a folder emptied by a consolidation. Staged; nothing is real until confirm. */
export const COCO_ORGANIZE_REMOVE_FOLDER_TOOL_KEY =
  'coco/organize-remove-folder' as const;
/**
 * The tools that read and edit the staged organize draft — loaded onto an
 * organize chat so Coco and the review panel act on the same plan. One list so
 * the server loader, the skill's tool gate, and the client seed stay in lockstep.
 */
export const COCO_ORGANIZE_DRAFT_TOOL_KEYS = [
  COCO_ORGANIZE_READ_DRAFT_TOOL_KEY,
  COCO_ORGANIZE_STAGE_MOVE_TOOL_KEY,
  COCO_ORGANIZE_ADD_FOLDER_TOOL_KEY,
  COCO_ORGANIZE_REMOVE_FOLDER_TOOL_KEY,
] as const;
/** Edit an existing document by UUID: JSON Pointer + value, or full document replace. */
export const COCO_EDIT_DOCUMENT_TOOL_KEY = 'coco/edit-document' as const;
/** Insert/replace a document response-shape node using json-type-builder JSONNode attrs. */
export const COCO_EDIT_STRUCTURED_OUTPUT_TOOL_KEY =
  'coco/edit-structured-output' as const;
/** Sub-agent for Cotera documents: create, edit, read, load, search. */
export const COCO_DOCUMENT_EDITOR_TOOL_KEY = 'coco/document-editor' as const;
/** Load integration tools or Cotera resources (chat, agent, folder, trigger) into the conversation. */
export const COCO_LOAD_RESOURCE_TOOL_KEY = 'coco/load-resource' as const;
export const COCO_FOR_EACH_TOOL_KEY = 'coco/for-each' as const;
export const COCO_ASK_FOR_CREDENTIALS_TOOL_KEY =
  'coco/ask-for-credentials' as const;
/**
 * Query everything stored for this chat — earlier tool results and attached
 * documents — with SQL and semantic search over the chat's ingot.
 */
export const COCO_INGOT_QUERY_TOOL_KEY = 'coco/ingot-query' as const;
/**
 * Fetch an Office document (Word .docx / PowerPoint .pptx) by URL and extract its
 * text. For office files *linked* in the prompt — not attached to the chat (those
 * go through the ingot upload flow). Skill-bundled by `coco/linked-office-document`
 * rather than always-on: it only makes sense once such a link is in play.
 */
export const COCO_READ_OFFICE_DOCUMENT_TOOL_KEY =
  'coco/read-office-document' as const;
export const COCO_AGENT_EDITOR_TOOL_KEY = 'coco/agent-editor' as const;
/**
 * Manager sub-agent for Cotera resources: routes agent vs folder work (create/edit/file/archive).
 * Does not replace `coco/cotera-agent` (user-defined runnable agents).
 */
export const COCO_COTERA_WORKSPACE_TOOL_KEY = 'coco/cotera-workspace' as const;
/** Sub-agent for folders: create/rename/move, and archive (approval-gated). */
export const COCO_FOLDER_EDITOR_TOOL_KEY = 'coco/folder-editor' as const;
export const COCO_FOLDER_CREATE_TOOL_KEY = 'coco/folder-create' as const;
export const COCO_FOLDER_RENAME_TOOL_KEY = 'coco/folder-rename' as const;
export const COCO_FOLDER_MOVE_RESOURCE_TOOL_KEY =
  'coco/folder-move-resource' as const;
/** Archives chat, agent, folder, trigger, or document via resource/archive; **mutation** (requires user approval). Not for integration tools. */
export const COCO_ARCHIVE_RESOURCE_TOOL_KEY = 'coco/archive-resource' as const;

/** Create a shareable link for one or more resources (access-grant or copy). */
export const COCO_CREATE_SHARE_LINK_TOOL_KEY =
  'coco/create-share-link' as const;

/**
 * Durably pause the current run for a fixed duration, then resume.
 * Backed by Restate's durable timer (ctx.sleep), so the wait survives process
 * restarts and replays without holding a worker. Driven from the ATC tool handler
 * at the workflow level (ctx.sleep cannot run inside a tool's ctx.run durable block).
 */
export const COCO_SLEEP_TOOL_KEY = 'coco/sleep' as const;

/** Relay a command to the connected Coco CLI on the user's local machine. */
export const COCO_RELAY_TOOL_KEY = 'coco/relay' as const;

/** Open an existing Cotera workflow as an artifact in the chat panel (read-only). */
export const COCO_OPEN_WORKFLOW_TOOL_KEY = 'coco/open-workflow' as const;
/** Sub-agent for Cotera workflows: open/render a workflow in the artifact panel. */
export const COCO_WORKFLOW_EDITOR_TOOL_KEY = 'coco/workflow-editor' as const;
/** Read a workflow's graph (nodes, edges, trigger bindings) so edits can be planned. */
export const COCO_READ_WORKFLOW_GRAPH_TOOL_KEY =
  'coco/read-workflow-graph' as const;
/** Create a new (empty) Cotera workflow and open it as a chat artifact. */
export const COCO_CREATE_WORKFLOW_TOOL_KEY = 'coco/create-workflow' as const;
/**
 * Add a node (agent / for-each / tool-call / filter / debounce / emit-event /
 * mapper / run-workflow / bucket / group-by)
 * to a workflow graph, optionally wiring an edge from an existing node. Authors
 * the node's inputs too: `params` + `paramBindings` for tool-call,
 * `payloadBindings` for emit-event, `fieldBindings` for mapper,
 * `inputBindings` for run-workflow, `bucketCountThreshold`/`bucketCron`
 * (+ optional `bucketAccumulate`/`bucketReducers`) for bucket, and
 * `groupByKeyPath`/`groupByItemsPath` for group-by, where a
 * binding ref is `<nodeId-or-__trigger__>/pointer`.
 */
export const COCO_ADD_WORKFLOW_NODE_TOOL_KEY =
  'coco/add-workflow-node' as const;
/** Update an existing workflow node's config data. */
export const COCO_UPDATE_WORKFLOW_NODE_TOOL_KEY =
  'coco/update-workflow-node' as const;
/** Delete a workflow node (and its connected edges). */
export const COCO_DELETE_WORKFLOW_NODE_TOOL_KEY =
  'coco/delete-workflow-node' as const;
/**
 * Release a debounce node's claim so a suppressed value fires again. The
 * override for "let this one through now" rather than waiting for the window.
 */
export const COCO_CLEAR_DEBOUNCE_CLAIMS_TOOL_KEY =
  'coco/clear-debounce-claims' as const;
/** Add an edge between two workflow nodes. */
export const COCO_ADD_WORKFLOW_EDGE_TOOL_KEY =
  'coco/add-workflow-edge' as const;
/** Delete a workflow edge. */
export const COCO_DELETE_WORKFLOW_EDGE_TOOL_KEY =
  'coco/delete-workflow-edge' as const;
/** Add a cron (scheduled) trigger to a workflow. */
export const COCO_ADD_WORKFLOW_CRON_TRIGGER_TOOL_KEY =
  'coco/add-workflow-cron-trigger' as const;
/** Remove a cron (scheduled) trigger from a workflow (detaches its binding). */
export const COCO_DELETE_WORKFLOW_CRON_TRIGGER_TOOL_KEY =
  'coco/delete-workflow-cron-trigger' as const;
/** Add a dataset (data-change) trigger to a workflow. */
export const COCO_ADD_WORKFLOW_DATASET_TRIGGER_TOOL_KEY =
  'coco/add-workflow-dataset-trigger' as const;
/** Remove a dataset trigger from a workflow (detaches its binding). */
export const COCO_DELETE_WORKFLOW_DATASET_TRIGGER_TOOL_KEY =
  'coco/delete-workflow-dataset-trigger' as const;

/** Structured search over a workflow's run history. Bundled by the `coco/workflow-run-search` skill. */
export const COCO_SEARCH_WORKFLOW_RUNS_TOOL_KEY =
  'coco/search-workflow-runs' as const;

/** Time-bucketed run counts per status for a workflow. Bundled by the `coco/workflow-run-search` skill. */
export const COCO_GET_WORKFLOW_RUN_STATS_TOOL_KEY =
  'coco/get-workflow-run-stats' as const;

/**
 * Progress and member runs for one run group — the batch a bulk dataset run
 * fans out into. Lets Coco reference the whole batch by the `groupId` that
 * `coco/run-dataset-workflows` hands back, instead of paging the workflow's run
 * history. Bundled by the `coco/workflow-run-search` skill.
 */
export const COCO_GET_WORKFLOW_RUN_GROUP_TOOL_KEY =
  'coco/get-workflow-run-group' as const;

/** Search cotera.co solution templates (marketing/sales/support agent templates). Bundled by the `coco/solution-templates` skill. */
export const COCO_SEARCH_TEMPLATES_TOOL_KEY = 'coco/search-templates' as const;
/** Fetch one cotera.co solution template's full content (prompt, steps, examples) by slug. Bundled by the `coco/solution-templates` skill. */
export const COCO_GET_TEMPLATE_TOOL_KEY = 'coco/get-template' as const;

/** Render an interactive chart artifact in Coco chat (Chart.js on the client). Bundled by the `coco/data-visualization` skill. */
export const COCO_RENDER_CHART_TOOL_KEY = 'coco/render-chart' as const;

/** Linked multi-chart dashboard with cross-filtering; artifact panel only. Re-use dashboard_id to append charts. */
export const COCO_RENDER_CHART_DASHBOARD_TOOL_KEY =
  'coco/render-chart-dashboard' as const;

/**
 * Dataset resource tools. Bundled by the `coco/dataset-editing` skill rather
 * than always-on: they only make sense once a dataset is in play, and the skill
 * carries the row-identity rules the write ones depend on.
 */
/**
 * Create a manual dataset from a declared set of columns, with optional
 * starting rows. Separate from the write tools because there is nothing to
 * address yet — no dataset id, no row ids — so it takes the whole shape at once
 * rather than a dataset to act on.
 */
export const COCO_DATASET_CREATE_TOOL_KEY = 'coco/dataset-create' as const;
/** Read a dataset's schema, row counts and a bounded window of its rows. */
export const COCO_DATASET_READ_TOOL_KEY = 'coco/dataset-read' as const;
/**
 * Write values into rows — existing ones by row id, or new ones by appending.
 * One key rather than a cell-shaped and a row-shaped one: `{ rowId, values }`
 * already expresses both, so splitting them only gave the model a choice to
 * get wrong.
 */
export const COCO_DATASET_WRITE_TOOL_KEY = 'coco/dataset-write' as const;
/** Structural edits: add, rename or delete columns; delete rows. */
export const COCO_DATASET_EDIT_TOOL_KEY = 'coco/dataset-edit' as const;
/**
 * Run the workflows bound to a dataset over its rows — the chat counterpart of
 * the grid's "Run" menu. Either replays every row that meets each trigger's
 * criterion (`scope: 'all'`) or force-fires only the rows a workflow still owes
 * a run (`scope: 'needs-run'`), optionally narrowed to some `workflowIds`.
 */
export const COCO_RUN_DATASET_WORKFLOWS_TOOL_KEY =
  'coco/run-dataset-workflows' as const;
/**
 * Create a dataset seeded from a SELECT run against the org's connected
 * warehouse. Separate from `coco/dataset-create`, which declares a manual
 * dataset from columns and rows: this one takes a query and a business key, and
 * the rows come from the warehouse asynchronously rather than from the call.
 */
export const COCO_DATASET_CREATE_WAREHOUSE_TOOL_KEY =
  'coco/dataset-create-warehouse' as const;
/**
 * List the tables and columns available in the org's connected warehouse, from
 * its information schema. What lets a warehouse dataset be created from a SELECT
 * the model actually knows will resolve, rather than one it guessed.
 */
export const COCO_WAREHOUSE_SCHEMA_TOOL_KEY = 'coco/warehouse-schema' as const;
/**
 * Change an existing warehouse dataset's SELECT and re-run its seed, in place.
 * The edit path for a warehouse dataset — including the retry after a failed
 * seed — so a corrected query re-seeds the same dataset instead of creating a
 * second one.
 */
export const COCO_DATASET_RESEED_WAREHOUSE_TOOL_KEY =
  'coco/dataset-reseed-warehouse' as const;
/**
 * The in-chat placeholder for a tabular file a user dropped into the chat: it is
 * uploaded into a chat-scoped dataset and this tool call, posted by the chat
 * turn, renders the "uploading…" → dataset-card progression. Not an
 * LLM-invocable tool — it is synthesized server-side and completed with the
 * dataset artifact when the import settles.
 */
export const COCO_UPLOAD_DATASET_TOOL_KEY = 'coco/upload-dataset' as const;
/**
 * The in-chat placeholder for a non-tabular document (pdf/text/markdown/html/
 * pptx) a user dropped into the chat: the bytes are uploaded to the chat's ingot
 * (never inlined into the model context), and this tool call — posted by the
 * chat turn — renders the "processing…" → "file ready" progression while Ingot
 * parses it. Not an LLM-invocable tool: synthesized server-side and completed by
 * {@link IngestChatFileSaga} when the parse settles, at which point the model is
 * told to read it via `coco/ingot-query`.
 */
export const COCO_UPLOAD_FILE_TOOL_KEY = 'coco/upload-file' as const;
/**
 * Save a chat-scoped uploaded dataset into the library (promote it). A file the
 * user dropped into the chat becomes a dataset hidden from the library; this is
 * how "save this upload as a dataset" is done — it does not recreate the rows,
 * it just makes the existing dataset a permanent library resource.
 */
export const COCO_PROMOTE_DATASET_TOOL_KEY = 'coco/promote-dataset' as const;

/** Google web search (Search API); included in default Coco tool keys—no load step required. */
export const COCO_WEB_SEARCH_TOOL_KEY = 'search-api/google-search' as const;
/** Site → markdown scrape; included in default Coco tool keys—no load step required. */
export const COCO_SITE_SCRAPER_TOOL_KEY = 'cotera/siteScraper/scrape' as const;

/** Builtin coco tool keys (system tools, not per-agent). */
export const COCO_BUILTIN_TOOL_KEYS = [
  'coco/sub-agent',
  COCO_COTERA_AGENT_TOOL_KEY,
  COCO_SEARCH_TOOL_KEY,
  COCO_LOAD_RESOURCE_TOOL_KEY,
  COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  COCO_INGOT_QUERY_TOOL_KEY,
  COCO_USER_OPTIONS_TOOL_KEY,
  COCO_SELECT_FOLDER_TOOL_KEY,
  COCO_CREATE_AGENT_TOOL_KEY,
  COCO_EDIT_AGENT_TOOL_KEY,
  COCO_READ_DOCUMENT_TOOL_KEY,
  COCO_READ_DOCUMENT_AS_MARKDOWN_TOOL_KEY,
  COCO_CREATE_DOCUMENT_TOOL_KEY,
  COCO_EDIT_DOCUMENT_TOOL_KEY,
  COCO_EDIT_STRUCTURED_OUTPUT_TOOL_KEY,
  COCO_DOCUMENT_EDITOR_TOOL_KEY,
  COCO_AGENT_EDITOR_TOOL_KEY,
  COCO_COTERA_WORKSPACE_TOOL_KEY,
  COCO_FOLDER_EDITOR_TOOL_KEY,
  COCO_FOLDER_CREATE_TOOL_KEY,
  COCO_FOLDER_RENAME_TOOL_KEY,
  COCO_FOLDER_MOVE_RESOURCE_TOOL_KEY,
  COCO_ARCHIVE_RESOURCE_TOOL_KEY,
  COCO_CREATE_SHARE_LINK_TOOL_KEY,
  COCO_SLEEP_TOOL_KEY,
  COCO_OPEN_WORKFLOW_TOOL_KEY,
  COCO_WORKFLOW_EDITOR_TOOL_KEY,
  COCO_READ_WORKFLOW_GRAPH_TOOL_KEY,
  COCO_CREATE_WORKFLOW_TOOL_KEY,
  COCO_ADD_WORKFLOW_NODE_TOOL_KEY,
  COCO_UPDATE_WORKFLOW_NODE_TOOL_KEY,
  COCO_DELETE_WORKFLOW_NODE_TOOL_KEY,
  COCO_CLEAR_DEBOUNCE_CLAIMS_TOOL_KEY,
  COCO_ADD_WORKFLOW_EDGE_TOOL_KEY,
  COCO_DELETE_WORKFLOW_EDGE_TOOL_KEY,
  COCO_ADD_WORKFLOW_CRON_TRIGGER_TOOL_KEY,
  COCO_DELETE_WORKFLOW_CRON_TRIGGER_TOOL_KEY,
  COCO_ADD_WORKFLOW_DATASET_TRIGGER_TOOL_KEY,
  COCO_DELETE_WORKFLOW_DATASET_TRIGGER_TOOL_KEY,
  COCO_FOR_EACH_TOOL_KEY,
  COCO_SEARCH_TEMPLATES_TOOL_KEY,
  COCO_GET_TEMPLATE_TOOL_KEY,
] as const;

export type CocoBuiltinToolKey = (typeof COCO_BUILTIN_TOOL_KEYS)[number];

/** Set of builtin coco tool keys for fast lookup. */
export const COCO_BUILTIN_TOOL_KEYS_SET = new Set<string>(
  COCO_BUILTIN_TOOL_KEYS
);

/**
 * Tool keys that change a resource but return no artifact of their own.
 *
 * Deliberately small, and deliberately *not* a register of "tools that edit
 * things". Any tool returning an `artifactResponse` states what it did in the
 * response itself, where the verb is a required argument, so one cannot be
 * added and forgotten. These are the leftovers with nowhere to put that
 * statement: a workflow is opened once and then rewired by node, edge and
 * trigger calls whose output is a plain result, and a dataset is edited the
 * same way. Without them the chat would describe an edited workflow by the
 * moment it was opened.
 *
 * Matched on the resource id in their *input*; see
 * {@link ARTIFACT_TARGET_ID_INPUT_FIELDS}.
 *
 * Content mutations only. Filing, archiving and promoting a resource move it or
 * change its status without touching what it *is*, and "Edited" over an agent
 * that was dragged into a folder is a worse answer than no verb.
 *
 * If one of these ever grows an artifact response, take it off this list and
 * let it declare `'edited'` instead.
 */
export const ARTIFACT_EDITING_TOOL_KEYS: ReadonlySet<string> = new Set<string>([
  COCO_FOLDER_RENAME_TOOL_KEY,
  COCO_ADD_WORKFLOW_NODE_TOOL_KEY,
  COCO_UPDATE_WORKFLOW_NODE_TOOL_KEY,
  COCO_DELETE_WORKFLOW_NODE_TOOL_KEY,
  COCO_ADD_WORKFLOW_EDGE_TOOL_KEY,
  COCO_DELETE_WORKFLOW_EDGE_TOOL_KEY,
  COCO_ADD_WORKFLOW_CRON_TRIGGER_TOOL_KEY,
  COCO_DELETE_WORKFLOW_CRON_TRIGGER_TOOL_KEY,
  COCO_ADD_WORKFLOW_DATASET_TRIGGER_TOOL_KEY,
  COCO_DELETE_WORKFLOW_DATASET_TRIGGER_TOOL_KEY,
  COCO_DATASET_WRITE_TOOL_KEY,
  COCO_DATASET_EDIT_TOOL_KEY,
  COCO_DATASET_RESEED_WAREHOUSE_TOOL_KEY,
]);

/**
 * Input fields naming the resource an {@link ARTIFACT_EDITING_TOOL_KEYS} call
 * acts on.
 *
 * Every editor tool spells its target the same way — `workflowId`, `agentId`,
 * `documentId`, `datasetId`, `folderId` — and for each of those kinds the
 * artifact id *is* the resource id (`artifactResponse('workflow', …, workflowId)`
 * and friends), so the value read here matches an artifact descriptor directly.
 *
 * Read only from tools already on the editing list, so a read tool that happens
 * to take a `workflowId` cannot mark anything edited.
 */
export const ARTIFACT_TARGET_ID_INPUT_FIELDS = [
  'agentId',
  'documentId',
  'workflowId',
  'datasetId',
  'folderId',
] as const;

/**
 * The resource ids an editing tool call names in its input.
 *
 * Returns nothing for a call still streaming its input, or one whose id field
 * came through blank — both are real states for a part read mid-turn, and
 * neither is evidence that anything was edited.
 */
export function artifactTargetIdsFromToolInput(
  input: Record<string, unknown>
): string[] {
  const ids: string[] = [];
  for (const field of ARTIFACT_TARGET_ID_INPUT_FIELDS) {
    const value = input[field];
    if (typeof value === 'string' && value.trim() !== '') {
      ids.push(value.trim());
    }
  }
  return ids;
}

/**
 * Tool keys that saved-agent sub-agents must not inherit from the agent config.
 * Search, load, for-each, and workspace are injected or delegated separately.
 */
export const SUB_AGENT_EXCLUDED_TOOL_KEYS = [
  COCO_SEARCH_TOOL_KEY,
  COCO_LOAD_RESOURCE_TOOL_KEY,
  COCO_FOR_EACH_TOOL_KEY,
  /** Sub-agents must delegate via specialists; never re-invoke the workspace manager. */
  COCO_COTERA_WORKSPACE_TOOL_KEY,
] as const;

/**
 * Tool keys hidden from `coco/search-cotera` discovery results.
 * These remain invocable when explicitly loaded/delegated.
 */
export const COCO_SEARCH_EXCLUDED_TOOL_KEYS = [] as const;

/**
 * Default coco tool key config used by Conversation when none is provided.
 * Includes always-available web utilities in addition to core coco tools.
 */
export const DEFAULT_COCO_TOOL_KEYS = {
  search: COCO_SEARCH_TOOL_KEY,
  loadResource: COCO_LOAD_RESOURCE_TOOL_KEY,
  webSearch: COCO_WEB_SEARCH_TOOL_KEY,
  siteScraper: COCO_SITE_SCRAPER_TOOL_KEY,
} as const;

/**
 * Tool keys always loaded for Coco conversations (discovery/loading).
 * Legacy default toolkit list; prompt tools are resolved via Conversation.getToolKeys().
 * `coco/for-each` is intentionally excluded here; it should only be used when
 * explicitly requested by the user or intentionally discovered and loaded.
 * Web search and site scraper ship on each LLM request via the SDK's DEFAULT_COCO_TOOL_KEYS
 * ({@link COCO_WEB_SEARCH_TOOL_KEY}, {@link COCO_SITE_SCRAPER_TOOL_KEY}), not this list.
 */
export const COCO_DEFAULT_LOADED_TOOL_KEYS = [
  COCO_LOAD_RESOURCE_TOOL_KEY,
  COCO_SEARCH_TOOL_KEY,
  COCO_INGOT_QUERY_TOOL_KEY,
] as const;

/**
 * Returns true if the tool key is a Coco (Cotera-included) tool.
 * Includes both builtin tools (coco/search-cotera, coco/load-resource, etc.) and
 * agent-specific tools (coco/sales-assistant, etc.).
 */
export function isCocoToolKey(key: string): boolean {
  return key.startsWith(COCO_TOOL_KEY_PREFIX);
}

/**
 * Returns true if the tool key is an agent tool (coco sub-agent or defined agent).
 * Excludes builtin coco tools like coco/search-cotera, coco/load-resource.
 */
export function isAgentToolKey(key: string): boolean {
  return (
    isCocoToolKey(key) &&
    key.startsWith(COCO_AGENT_TOOL_KEY_PREFIX) &&
    !COCO_BUILTIN_TOOL_KEYS_SET.has(key)
  );
}

const AGENT_KEY_PREFIX = 'agent-';

/**
 * Derive the agent tool key from an agent ID or agent key.
 * Canonical conversion used by Agent.createNew and agent-tool-key.toAgentToolKey.
 * Accepts either a UUID (e.g. "546bef41-a1ce-436d-ab28-682fe6462c42") or
 * an agent key (e.g. "agent-546bef41a1ce436dab28682fe6462c42").
 */
export function agentIdToAgentToolKey(agentIdOrKey: string): string {
  const idPart = agentIdOrKey.startsWith(AGENT_KEY_PREFIX)
    ? agentIdOrKey.slice(AGENT_KEY_PREFIX.length)
    : agentIdOrKey;
  const agentKey = `${AGENT_KEY_PREFIX}${idPart.replace(/-/g, '')}`;
  return `${COCO_TOOL_KEY_PREFIX}${agentKey}`;
}

const WORKFLOW_ID_HYPHEN_POSITIONS = [8, 12, 16, 20] as const;

function compactUuidToUuid(compact: string): string | null {
  if (compact.length !== 32 || !/^[0-9a-f]+$/i.test(compact)) {
    return null;
  }
  const parts: string[] = [];
  let start = 0;
  for (const pos of WORKFLOW_ID_HYPHEN_POSITIONS) {
    parts.push(compact.slice(start, pos));
    start = pos;
  }
  parts.push(compact.slice(start));
  return parts.join('-');
}

/**
 * The agent UUID an agent tool key names, or null when the key is not one.
 *
 * The inverse of {@link agentIdToAgentToolKey}, for callers that hold a tool key
 * and need the resource behind it — e.g. telling a workflow editor that
 * `coco/agent-<id>` should have been an `agent` node on that agent.
 */
export function agentIdFromToolKey(toolKey: string): string | null {
  if (!isAgentToolKey(toolKey)) {
    return null;
  }
  return compactUuidToUuid(toolKey.slice(COCO_AGENT_TOOL_KEY_PREFIX.length));
}

/**
 * Derive the workflow tool key from a workflow UUID.
 * Uses hyphen-stripped id: coco/workflow-<uuid-without-hyphens>.
 */
export function workflowIdToWorkflowToolKey(workflowId: string): string {
  return `${COCO_WORKFLOW_TOOL_KEY_PREFIX}${workflowId.replace(/-/g, '')}`;
}

export function isWorkflowToolKey(key: string): boolean {
  return (
    isCocoToolKey(key) &&
    key.startsWith(COCO_WORKFLOW_TOOL_KEY_PREFIX) &&
    key.length > COCO_WORKFLOW_TOOL_KEY_PREFIX.length
  );
}

export function workflowIdFromToolKey(toolKey: string): string | null {
  if (!isWorkflowToolKey(toolKey)) {
    return null;
  }
  return compactUuidToUuid(toolKey.slice(COCO_WORKFLOW_TOOL_KEY_PREFIX.length));
}

/**
 * Collapse per-instance agent/workflow tool keys to a stable label for metrics.
 * Keeps cardinality bounded (one series per tool family instead of per resource).
 */
export function toolKeyForMetrics(toolKey: string): string {
  if (isAgentToolKey(toolKey)) {
    return COCO_COTERA_AGENT_TOOL_KEY;
  }
  if (isWorkflowToolKey(toolKey)) {
    return COCO_COTERA_WORKFLOW_TOOL_KEY;
  }
  return toolKey;
}
